/**
 * MetaSocialConnectCard — Vendor / Pro dashboard widget to connect
 * Facebook + Instagram Business accounts, import their portfolio from
 * social, and auto-post booking confirmations to their socials.
 *
 * All token handling happens server-side. This component only sees
 * boolean status + Page name (no tokens).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  InstagramLogo, FacebookLogo, CloudArrowDown, LinkBreak, Plug, Warning,
  CheckCircle, Spinner,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MetaSocialConnectCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/meta/connection-status`,
        { withCredentials: true },
      );
      setStatus(data);
    } catch (err) {
      // 401 / 403 simply hide the card
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        setStatus({ configured: false, connected: false, ineligible: true });
      } else {
        setStatus({ configured: false, connected: false });
      }
    }
  };

  useEffect(() => { load(); }, []);

  // Consume OAuth redirect query params
  useEffect(() => {
    const metaStatus = searchParams.get('meta_status');
    if (!metaStatus) return;
    if (metaStatus === 'success') {
      toast.success('Connected to Meta — your portfolio can now import from Instagram & Facebook');
    } else {
      const reason = searchParams.get('meta_reason') || 'unknown_error';
      toast.error(`Meta connection failed: ${reason}`);
    }
    // Clean URL
    searchParams.delete('meta_status');
    searchParams.delete('meta_reason');
    setSearchParams(searchParams, { replace: true });
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/meta/login-url`,
        { withCredentials: true },
      );
      if (data?.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.error('Failed to get authorization URL');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start Meta connection');
    }
    setLoading(false);
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect your Meta account? Future booking auto-posts will stop until you reconnect.')) return;
    try {
      await axios.post(`${BACKEND_URL}/api/meta/disconnect`, {}, { withCredentials: true });
      toast.success('Disconnected');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Disconnect failed');
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/meta/portfolio/import`,
        {},
        { withCredentials: true },
      );
      if (data.skipped_cap) {
        toast.warning(data.message || 'Photo cap already reached');
      } else if (data.imported === 0) {
        toast.info('No new photos found to import');
      } else {
        toast.success(`Imported ${data.imported} photo${data.imported === 1 ? '' : 's'} from Meta`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    }
    setImporting(false);
  };

  if (!status) {
    return null;
  }
  if (status.ineligible) {
    return null;
  }
  if (!status.configured) {
    return (
      <Card className="bg-white border-[#E8E1D5]" data-testid="meta-social-card">
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <InstagramLogo size={20} className="text-brand-primary" />
            <FacebookLogo size={20} className="text-brand-primary" />
            Social Media
          </CardTitle>
          <CardDescription>
            Meta integration is not configured on the server yet. Admin needs to add
            Facebook App credentials to enable Instagram + Facebook Page connect.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const connected = !!status.connected;

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="meta-social-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <InstagramLogo size={20} className="text-pink-600" weight="fill" />
          <FacebookLogo size={20} className="text-blue-600" weight="fill" />
          Social Media — Instagram &amp; Facebook
        </CardTitle>
        <CardDescription>
          Connect your business Instagram + Facebook Page to (a) auto-import recent
          photos into your BeeWeddy portfolio and (b) automatically announce confirmed
          bookings on both feeds.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!connected ? (
          <div className="space-y-3">
            <p className="text-sm text-text-secondary">
              You'll be redirected to Facebook to authorize BeeWeddy. We only request the
              minimum needed permissions and tokens are encrypted on our servers.
            </p>
            <Button onClick={handleConnect} disabled={loading}
              className="rounded-none bg-gradient-to-r from-pink-600 via-purple-600 to-blue-600 text-white hover:opacity-90"
              data-testid="meta-connect-btn">
              {loading ? <Spinner size={14} className="mr-2 animate-spin" /> : <Plug size={14} className="mr-2" />}
              Connect Instagram &amp; Facebook
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-2 p-3 border border-[#E8E1D5] bg-sand-50">
                <InstagramLogo size={22} className={status.has_instagram ? 'text-pink-600' : 'text-stone-400'} weight="fill" />
                <div className="text-sm flex-1 min-w-0">
                  <div className="font-semibold">Instagram Business</div>
                  <div className={`text-xs ${status.has_instagram ? 'text-green-700' : 'text-amber-700'}`}>
                    {status.has_instagram
                      ? <><CheckCircle size={12} className="inline mr-1" /> Linked</>
                      : <><Warning size={12} className="inline mr-1" /> Not linked to your Page</>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 border border-[#E8E1D5] bg-sand-50">
                <FacebookLogo size={22} className={status.has_facebook_page ? 'text-blue-600' : 'text-stone-400'} weight="fill" />
                <div className="text-sm flex-1 min-w-0">
                  <div className="font-semibold">Facebook Page</div>
                  <div className={`text-xs ${status.has_facebook_page ? 'text-green-700' : 'text-amber-700'} truncate`}>
                    {status.has_facebook_page
                      ? <><CheckCircle size={12} className="inline mr-1" /> {status.fb_page_name || 'Connected'}</>
                      : <><Warning size={12} className="inline mr-1" /> Not connected</>}
                  </div>
                </div>
              </div>
            </div>

            {status.expiring_soon && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200">
                <Warning size={16} className="text-amber-700 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">
                  Your Meta access is expiring soon. Re-connect now to keep auto-posting working.
                  <Button onClick={handleConnect} variant="link" className="ml-1 p-0 h-auto text-xs text-amber-900 underline" data-testid="meta-reconnect-btn">
                    Reconnect →
                  </Button>
                </p>
              </div>
            )}

            {status.last_error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200">
                <Warning size={16} className="text-red-700 shrink-0 mt-0.5" />
                <p className="text-xs text-red-800">
                  <span className="font-semibold">Last error:</span> {status.last_error}
                </p>
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-2 border-t border-[#E8E1D5]">
              <Button onClick={handleImport} disabled={importing}
                className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50"
                data-testid="meta-import-btn">
                {importing ? <Spinner size={14} className="mr-2 animate-spin" /> : <CloudArrowDown size={14} className="mr-2" />}
                Import recent photos
              </Button>
              <Button onClick={handleConnect} variant="outline" disabled={loading}
                className="rounded-none border-[#E8E1D5]"
                data-testid="meta-reauth-btn">
                <Plug size={14} className="mr-2" /> Re-authorize
              </Button>
              <Button onClick={handleDisconnect} variant="outline"
                className="rounded-none border-[#E8E1D5] text-red-600 hover:bg-red-50"
                data-testid="meta-disconnect-btn">
                <LinkBreak size={14} className="mr-2" /> Disconnect
              </Button>
            </div>
            <p className="text-[11px] text-text-secondary italic">
              Auto-posting fires when you accept a vendor offer for a confirmed booking. Posts
              use one of your portfolio photos as the hero image.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MetaSocialConnectCard;
